import React, { useState } from "react";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import emailjs from "@emailjs/browser";
import { useT } from "@/lib/i18n";

/**
 * SalesShark Kapcsolat űrlap
 *
 * EmailJS template változók (a sablonban ezekre hivatkozz {{...}} formában):
 *   {{from_name}}   — küldő neve
 *   {{from_email}}  — küldő e-mail címe
 *   {{phone}}       — telefonszám (opcionális)
 *   {{company}}     — cégnév (opcionális)
 *   {{message}}     — üzenet szövege
 *   {{page_url}}    — melyik oldalról küldte
 *   {{submitted_at}}— beküldés időpontja (ISO)
 *   {{reply_to}}    — válasz-cím (= from_email)
 *
 * EmailJS konfiguráció env változóval (frontend/.env):
 *   REACT_APP_EMAILJS_SERVICE_ID
 *   REACT_APP_EMAILJS_TEMPLATE_ID
 *   REACT_APP_EMAILJS_PUBLIC_KEY
 */
export default function ContactForm() {
    const { t } = useT();
    const [form, setForm] = useState({
        from_name: "",
        from_email: "",
        phone: "",
        company: "",
        message: "",
    });
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");

        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

        const templateParams = {
            from_name: form.from_name,
            from_email: form.from_email,
            phone: form.phone,
            company: form.company,
            message: form.message,
            page_url: typeof window !== "undefined" ? window.location.href : "",
            submitted_at: new Date().toISOString(),
            reply_to: form.from_email,
        };

        try {
            if (!serviceId || !templateId || !publicKey) {
                // Az EmailJS keys még nincs beállítva — csak logolunk és sikert jelzünk.
                // Miután a `.env`-be bekerülnek a kulcsok, ez automatikusan él.
                console.info(
                    "[ContactForm] EmailJS keys not set — form data:",
                    templateParams,
                );
                await new Promise((r) => setTimeout(r, 700));
            } else {
                await emailjs.send(
                    serviceId,
                    templateId,
                    templateParams,
                    publicKey,
                );
            }
            setStatus("success");
            setForm({
                from_name: "",
                from_email: "",
                phone: "",
                company: "",
                message: "",
            });
        } catch (err) {
            setStatus("error");
            setErrorMsg(err?.text || err?.message || "Ismeretlen hiba.");
        }
    };

    return (
        <form
            onSubmit={onSubmit}
            className="p-6 md:p-8 lg:p-10 rounded-lg border border-white/10 bg-[color:var(--bg-base)]/60"
            data-testid="contact-form"
        >
            <div className="text-[color:var(--accent)] text-xs uppercase tracking-widest font-semibold">
                {t.form.eyebrow}
            </div>
            <h3 className="mt-2 font-display text-2xl md:text-3xl text-white tracking-tight leading-tight">
                {t.form.heading}
            </h3>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-widest text-white/50 font-medium">
                        {t.form.labels.name} *
                    </span>
                    <input
                        type="text"
                        name="from_name"
                        required
                        value={form.from_name}
                        onChange={onChange}
                        placeholder={t.form.placeholders.name}
                        className="bg-transparent border border-white/12 hover:border-white/25 focus:border-[color:var(--accent)] focus:outline-none rounded-md px-4 py-3 text-white placeholder-white/30 text-[0.95rem] transition-colors"
                        data-testid="form-input-name"
                    />
                </label>

                <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-widest text-white/50 font-medium">
                        {t.form.labels.email} *
                    </span>
                    <input
                        type="email"
                        name="from_email"
                        required
                        value={form.from_email}
                        onChange={onChange}
                        placeholder={t.form.placeholders.email}
                        className="bg-transparent border border-white/12 hover:border-white/25 focus:border-[color:var(--accent)] focus:outline-none rounded-md px-4 py-3 text-white placeholder-white/30 text-[0.95rem] transition-colors"
                        data-testid="form-input-email"
                    />
                </label>

                <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-widest text-white/50 font-medium">
                        {t.form.labels.phone} *
                    </span>
                    <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={onChange}
                        placeholder={t.form.placeholders.phone}
                        className="bg-transparent border border-white/12 hover:border-white/25 focus:border-[color:var(--accent)] focus:outline-none rounded-md px-4 py-3 text-white placeholder-white/30 text-[0.95rem] transition-colors"
                        data-testid="form-input-phone"
                    />
                </label>

                <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-widest text-white/50 font-medium">
                        {t.form.labels.company}
                    </span>
                    <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={onChange}
                        placeholder={t.form.placeholders.company}
                        className="bg-transparent border border-white/12 hover:border-white/25 focus:border-[color:var(--accent)] focus:outline-none rounded-md px-4 py-3 text-white placeholder-white/30 text-[0.95rem] transition-colors"
                        data-testid="form-input-company"
                    />
                    <span className="text-[11px] text-white/40 leading-snug">
                        {t.form.companyHelper}
                    </span>
                </label>

                <label className="flex flex-col gap-2 md:col-span-2">
                    <span className="text-xs uppercase tracking-widest text-white/50 font-medium">
                        {t.form.labels.message} *
                    </span>
                    <textarea
                        name="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={onChange}
                        placeholder={t.form.placeholders.message}
                        className="bg-transparent border border-white/12 hover:border-white/25 focus:border-[color:var(--accent)] focus:outline-none rounded-md px-4 py-3 text-white placeholder-white/30 text-[0.95rem] transition-colors resize-y min-h-[120px]"
                        data-testid="form-input-message"
                    />
                </label>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <p className="text-white/40 text-xs leading-relaxed max-w-md">
                    {t.form.disclaimer}
                </p>
                <button
                    type="submit"
                    disabled={status === "sending"}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="form-submit"
                >
                    {status === "sending" ? (
                        <>
                            <Loader2 size={16} className="animate-spin" />
                            <span>{t.form.sending}</span>
                        </>
                    ) : (
                        <>
                            <span>{t.form.submit}</span>
                            <Send size={14} strokeWidth={2} />
                        </>
                    )}
                </button>
            </div>

            {status === "success" && (
                <div
                    className="mt-6 flex items-start gap-3 p-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    data-testid="form-success"
                >
                    <CheckCircle2
                        size={20}
                        strokeWidth={1.75}
                        className="shrink-0 mt-0.5"
                    />
                    <div className="text-sm leading-relaxed">
                        <div className="font-medium">
                            {t.form.successTitle}
                        </div>
                        <div className="text-emerald-200/80">
                            {t.form.successText}
                        </div>
                    </div>
                </div>
            )}

            {status === "error" && (
                <div
                    className="mt-6 flex items-start gap-3 p-4 rounded-md border border-red-500/30 bg-red-500/10 text-red-200"
                    data-testid="form-error"
                >
                    <AlertCircle
                        size={20}
                        strokeWidth={1.75}
                        className="shrink-0 mt-0.5"
                    />
                    <div className="text-sm leading-relaxed">
                        <div className="font-medium">{t.form.errorTitle}</div>
                        <div className="text-red-200/80">
                            {t.form.errorText}
                            {errorMsg ? ` (${errorMsg})` : ""}
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
}
